import { format } from 'date-fns';
import { CalendarIcon, CheckCircle, Clock, FileText, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PLATFORM_DISPLAY_NAMES, PLATFORM_ICONS } from '../../content-calendar/constants';
import type { Platform, PostStatus } from '../../content-calendar/types';

const STATUS_ICONS: Record<PostStatus, any> = {
  draft: FileText,
  approved: CheckCircle,
  scheduled: Clock,
  posted: Send,
};

const STATUS_LABELS: Record<PostStatus, string> = {
  draft: 'Draft',
  approved: 'Approved',
  scheduled: 'Scheduled',
  posted: 'Posted',
};

const STATUS_COLORS: Record<PostStatus, string> = {
  draft: 'text-muted-foreground',
  approved: 'text-green-600 dark:text-green-500',
  scheduled: 'text-amber-600 dark:text-amber-500',
  posted: 'text-blue-600 dark:text-blue-500',
};

const PLATFORMS: Platform[] = [
  'facebook',
  'instagram',
  'twitter',
  'linkedin',
  'youtube',
  'tiktok',
  'pinterest',
  'google_my_business',
  'threads'
];

export interface SchedulingControlsProps {
  date: string;
  time: string;
  platform: Platform;
  status: PostStatus;
  pageName?: string;
  onDateChange: (date: string) => void;
  onTimeChange: (time: string) => void;
  onPlatformChange: (platform: Platform) => void;
  onStatusChange: (status: PostStatus) => void;
}

export function SchedulingControls({
  date,
  time,
  platform,
  status,
  pageName,
  onDateChange,
  onTimeChange,
  onPlatformChange,
  onStatusChange,
}: SchedulingControlsProps) {
  return (
    <div className="space-y-2">
      {/* POST SCHEDULING */}
      <div className="p-4 border border-border rounded-lg bg-card space-y-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Schedule
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium mb-2 block">Date</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal bg-background"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? (() => {
                    try {
                      // Parse date string safely, handling ISO format
                      const dateStr = date.includes('T')
                        ? date.split('T')[0]
                        : date;
                      return format(new Date(dateStr + 'T12:00:00'), 'MMM dd, yyyy');
                    } catch {
                      return date;
                    }
                  })() : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="start">
                <Calendar
                  mode="single"
                  className='rounded-md w-full'
                  selected={date ? (() => {
                    try {
                      // Handle both ISO strings and date objects
                      const dateStr = date.includes('T')
                        ? date.split('T')[0]
                        : date;
                      const [year, month, day] = dateStr.split('-').map(Number);
                      return new Date(year, month - 1, day); // Use local timezone
                    } catch {
                      return undefined;
                    }
                  })() : undefined}
                  onSelect={(selectedDate) => {
                    if (selectedDate) {
                      // Format to YYYY-MM-DD using local timezone
                      const year = selectedDate.getFullYear();
                      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
                      const day = String(selectedDate.getDate()).padStart(2, '0');
                      onDateChange(`${year}-${month}-${day}`);
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Time</label>
            <input
              type="time"
              value={time}
              onChange={(e) => onTimeChange(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            />
          </div>
        </div>

        {/* Account Selector */}
        <div>
          <label className="text-sm font-medium mb-2 block">Account</label>
          <Select
            value={platform}
            onValueChange={(value) => onPlatformChange(value as Platform)}
          >
            <SelectTrigger className="bg-background">
              <SelectValue>
                <div className="flex items-center gap-2">
                  <img
                    src={PLATFORM_ICONS[platform]}
                    alt=""
                    className="w-4 h-4"
                  />
                  <span className="truncate">
                    {pageName || `${PLATFORM_DISPLAY_NAMES[platform]} Page`}
                  </span>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {PLATFORMS.map((plat) => (
                <SelectItem key={plat} value={plat}>
                  <div className="flex items-center gap-2">
                    <img
                      src={PLATFORM_ICONS[plat]}
                      alt=""
                      className="w-4 h-4"
                    />
                    <span>
                      {pageName || `${PLATFORM_DISPLAY_NAMES[plat]} Page`}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* POST STATUS */}
      <div className="p-4 border border-border rounded-lg bg-card">
        <label className="text-sm font-medium mb-2 block">Status</label>
        <Select
          value={status}
          onValueChange={(value) => onStatusChange(value as PostStatus)}
        >
          <SelectTrigger className="bg-background">
            <SelectValue>
              <div className="flex items-center gap-2">
                {(() => {
                  const StatusIcon = STATUS_ICONS[status];
                  const colorClass = STATUS_COLORS[status];
                  return <StatusIcon className={`w-4 h-4 ${colorClass}`} />;
                })()}
                <span>{STATUS_LABELS[status]}</span>
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(STATUS_ICONS) as PostStatus[]).map((stat) => {
              const StatusIcon = STATUS_ICONS[stat];
              const colorClass = STATUS_COLORS[stat];
              return (
                <SelectItem key={stat} value={stat}>
                  <div className="flex items-center gap-2">
                    <StatusIcon className={`w-4 h-4 ${colorClass}`} />
                    <span>{STATUS_LABELS[stat]}</span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
